import { FormControl, FormGroup, FormLabel, Grid } from "@mui/material";
import type { FC } from "react";
import type { Control } from "react-hook-form";
import FormSelect from "../../components/formComponents/FormSelect";
import FormSwitch from "../../components/formComponents/FormSwitch";
import FormTextField, { emailValidationPattern } from "../../components/formComponents/FormTextField";
import type { Department } from "./agentsApi";
import { DEPARTMENT_OPTIONS } from "./departmentOptions";

interface Props {
  readonly control: Control<AgentFormState>;
  readonly isAdmin: boolean;
}

export interface AgentFormState {
  readonly name: string;
  readonly phone: string;
  readonly przezmorzeEmail: string;
  readonly b2bEmail: string;
  readonly loginEmail: string;
  readonly privileges: {
    onlyOwnCustomersOrders: boolean;
    onlyPublicAttachments: boolean;
    onlyOwnCustomersEdit: boolean;
    cannotChangeOptionPrices: boolean;
    cannotExportOrders: boolean;
    canViewProvidersAndProducts: boolean;
    canChangeCustomerAccountManager: boolean;
    canEditProvidersProductsAndDictionaries: boolean;
    canEditPriceLists: boolean;
    canEditPackages: boolean;
    canDeleteCustomerNotes: boolean;
    cannotViewCustomers: boolean;
    canViewStatistics: boolean;
  };
  readonly isAdmin: boolean;
  readonly department: Department | "";
}

const AgentEdit: FC<Props> = ({ control, isAdmin }) => {
  return (
    <Grid
      container
      size={12}
      spacing={2}
      sx={{
        marginTop: "8px",
      }}
    >
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          label="Imię i nazwisko"
          control={control}
          name="name"
          rules={{ required: { value: true, message: "Nazwa przedstawiciela nie może być pusta" } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          label="Login (Google mail)"
          control={control}
          type="email"
          name="loginEmail"
          rules={{
            pattern: emailValidationPattern,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          label="Email kontaktowy Przezmorze"
          control={control}
          type="email"
          name="przezmorzeEmail"
          rules={{
            pattern: emailValidationPattern,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          label="Email kontaktowy B2B"
          control={control}
          type="email"
          name="b2bEmail"
          rules={{
            pattern: emailValidationPattern,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField label="Telefon" control={control} type="phone" name="phone" />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormSelect label="Dział" control={control} name="department" options={DEPARTMENT_OPTIONS} />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl component="fieldset" variant="standard">
          <FormLabel component="legend">Rola</FormLabel>
          <FormGroup>
            <FormSwitch control={control} name="isAdmin" label="Dostęp admin" />
          </FormGroup>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl component="fieldset" variant="standard" disabled={isAdmin}>
          <FormLabel component="legend">Uprawnienia</FormLabel>
          <FormGroup>
            <FormSwitch
              control={control}
              name="privileges.onlyOwnCustomersOrders"
              label="Widzi jedynie zamówienia swoich klientów + e-commerce"
            />
            <FormSwitch
              control={control}
              name="privileges.onlyPublicAttachments"
              label="Widzi jedynie załączniki widoczne dla klientów"
            />
            <FormSwitch
              control={control}
              name="privileges.onlyOwnCustomersEdit"
              label="Może edytować jedynie opisy/notatki swoich klientów (w zakładce klient)"
            />
            <FormSwitch
              control={control}
              name="privileges.cannotExportOrders"
              label="Nie ma dostępu do raportów zamówienia/produkty z zakładki zamówienia"
            />
            <FormSwitch
              control={control}
              name="privileges.cannotChangeOptionPrices"
              label="Nie może zmieniać cen zasobów w zamówieniach i kalkulacjach"
            />
            <FormSwitch
              control={control}
              name="privileges.canViewProvidersAndProducts"
              label="Może przeglądać dostawców i produkty"
            />
            <FormSwitch
              control={control}
              name="privileges.canChangeCustomerAccountManager"
              label="Może zmieniać opiekuna klienta"
            />
            <FormSwitch
              control={control}
              name="privileges.canEditProvidersProductsAndDictionaries"
              label="Może edytować dostawców, produkty i słowniki"
            />
            <FormSwitch
              control={control}
              name="privileges.canEditPriceLists"
              label="Może edytować cenniki (w tym import)"
            />
            <FormSwitch control={control} name="privileges.canEditPackages" label="Może edytować pakiety" />
            <FormSwitch
              control={control}
              name="privileges.canDeleteCustomerNotes"
              label="Może kasować notatki klientów"
            />
            <FormSwitch
              control={control}
              name="privileges.cannotViewCustomers"
              label="Nie ma dostępu do zakładki Klienci (bazy klientów)"
            />
            <FormSwitch control={control} name="privileges.canViewStatistics" label="Widzi raporty" />
          </FormGroup>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default AgentEdit;
